import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClipboardList, UserCheck, Users, ArrowRight,Package } from 'lucide-react';
import COEHeader from './COEHeader';
import logoutManager from '../logout';
import api from '../api';

const cards = [
  {
    title: 'Assign Paper To Subject',
    description: 'Map approved paper templates to subjects for the selected academic context.',
    icon: ClipboardList,
    color: 'indigo',
    path: '/coe/assign-paper-to-subject',
    cta: 'Open Paper Assignment'
  },
  {
    title: 'Assign Evaluator To Subject',
    description: 'Allocate evaluators to subjects to streamline assessment and marking workflows.',
    icon: UserCheck,
    color: 'emerald',
    path: '/coe/assign-evaluator-to-subject',  // Added path
    cta: 'Assign Evaluator'
  },
  {
    title: 'Manage Evaluators',
    description: 'Create and maintain evaluator assignments, availability, and ownership details.',
    icon: Users,
    color: 'blue',
    path: '/coe/manage-evaluators',  
    cta: 'Manage Evaluators'
  },
    {
    title: 'Packet Manager',  
    description: 'Create answer script packets and assign to evaluators.',
    icon: Package,
    color: 'purple',
    path: '/coe/packet-manager',
    cta: 'Create Packets'
  }
];

const EvaluationAssignment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useState(location.state?.user || null);
  const [tenant, setTenant] = useState(location.state?.tenant || null);
  const [orgInfo, setOrgInfo] = useState(location.state?.orgInfo || { name: '', logoUrl: '' });

  useEffect(() => {
    if (!tenant) {
      setTenant({
        name: localStorage.getItem('tenantName') || '',
        subdomain: localStorage.getItem('tenantSubdomain') || '',
        id: localStorage.getItem('tenantId') || ''
      });
    }
  }, [tenant]);

  useEffect(() => {
    const tenantId = localStorage.getItem('tenantId') || tenant?.id || tenant?._id;
    if (tenantId) {
      api.get(`/api/organization/basic-info/${tenantId}`)
        .then((res) => setOrgInfo({ name: res.data?.name || '', logoUrl: res.data?.logoUrl || '' }))
        .catch(() => setOrgInfo({ name: '', logoUrl: '' }));
    }
  }, [tenant]);

  const handleLogout = () => logoutManager.logout({ redirectTo: '/' });

  const handleCardClick = (card) => {
    if (!card.path) return;
    navigate(card.path, { state: { user, tenant, orgInfo } });
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F9FAFB] selection:bg-indigo-100">
      <COEHeader user={user} tenant={tenant} orgInfo={orgInfo} onLogout={handleLogout} />

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="mb-8 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight">Paper &amp; Evaluator Assignment</h1>
          <p className="mt-2 text-sm md:text-base text-slate-500 font-medium">
            Configure paper-template mapping and evaluator ownership for subject-level assessment operations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-7">
          {cards.map((card, index) => {
            const Icon = card.icon;
            const isClickable = Boolean(card.path);

            return (
              <button
                key={index}
                type="button"
                onClick={() => handleCardClick(card)}
                disabled={!isClickable}
                className={`group flex flex-col text-left p-6 rounded-3xl border bg-white transition-all duration-300 ${
                  isClickable
                    ? 'border-slate-100 hover:-translate-y-1 hover:shadow-2xl hover:shadow-indigo-100 cursor-pointer'
                    : 'border-slate-200 opacity-90 cursor-not-allowed'
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-2xl mb-5 flex items-center justify-center ${
                    card.color === 'indigo' ? 'bg-indigo-50 text-indigo-600' : ''
                  } ${card.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' : ''} ${
                    card.color === 'blue' ? 'bg-blue-50 text-blue-600' : ''
                  }`}
                >
                  <Icon className="w-6 h-6" />
                </div>

                <h3 className="text-lg font-bold text-slate-800 tracking-tight">{card.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed flex-1">{card.description}</p>

                <div className="mt-6 flex items-center justify-between">
                  <span className={`text-[11px] font-black uppercase tracking-widest ${isClickable ? 'text-indigo-600' : 'text-slate-400'}`}>
                    {card.cta}
                  </span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isClickable ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default EvaluationAssignment;